import React, { createContext, useState, useEffect, useCallback } from 'react';
import { getStorage, setStorage, clearStorage } from '../utilities/storage';
import {
  login as apiLogin,
  quickAccess as apiQuickAccess,
  logout as apiLogout,
  register as apiRegister
} from '../api/auth';
import api from '../api';

export const AuthContext = createContext();

const defaultSocial = {
  facebook: '',
  instagram: '',
  linkedin: '',
  tiktok: ''
};

const defaultSettings = {
  showProfile: true,
  showCourses: true
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatUser = useCallback((user) => {
    const rawName = typeof user?.name === 'string'
      ? user.name
      : [user?.first_name, user?.last_name].filter(Boolean).join(' ');
    const nameParts = (rawName || 'Usuario').trim().split(/\s+/);
    const firstName = user?.first_name || nameParts[0] || 'Usuario';
    const lastName = user?.last_name || nameParts.slice(1).join(' ');

    return {
      id: user?.id,
      name: rawName || `${firstName} ${lastName}`.trim(),
      username: user?.email?.split('@')[0] || 'usuario',
      email: user?.email,
      first_name: firstName,
      last_name: lastName,
      profile_picture: user?.profile_picture || 'default.jpg',
      role: user?.role || 'student',
      account_status: user?.account_status || 'active',
      bio: user?.bio || '',
      title: user?.title || '',
      language: user?.language || 'Español',
      social: { ...defaultSocial, ...(user?.social || {}) },
      settings: { ...defaultSettings, ...(user?.settings || {}) },
      studentCourses: Array.isArray(user?.studentCourses) ? user.studentCourses : [],
      instructorCourses: Array.isArray(user?.instructorCourses) ? user.instructorCourses : [],
      token: user?.token
    };
  }, []);

  const applyAuthenticatedUser = useCallback((user, token) => {
    const formattedUser = formatUser({ ...user, token });
    setCurrentUser(formattedUser);
    setStorage('currentUser', formattedUser);
    if (token) {
      setStorage('authToken', token);
    }
    setIsAuthenticated(true);
    setError(null);
    return formattedUser;
  }, [formatUser]);

  const clearAuth = useCallback(() => {
    clearStorage('authToken');
    clearStorage('currentUser');
    setCurrentUser(null);
    setIsAuthenticated(false);
  }, []);

  const fetchCurrentUser = useCallback(async (token) => {
    const response = await api.get('/users/me');
    const userData = response.data?.success ? response.data.data : response.data;
    return applyAuthenticatedUser(userData, token || getStorage('authToken'));
  }, [applyAuthenticatedUser]);

  const verifyAuth = useCallback(async () => {
    const token = getStorage('authToken');
    const storedUser = getStorage('currentUser');

    if (!token || !storedUser) {
      setIsLoading(false);
      return;
    }

    try {
      await fetchCurrentUser(token);
    } catch (verifyError) {
      console.error('Error verifying token:', verifyError);
      setError('Error al verificar la sesión');
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  }, [clearAuth, fetchCurrentUser]);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  const login = async (email, password) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiLogin(email, password);

      if (data?.data?.user && data.data.token) {
        const token = data.data.token;
        applyAuthenticatedUser(data.data.user, token);
        const hydratedUser = await fetchCurrentUser(token);
        return { success: true, user: hydratedUser };
      }

      const message = data?.message || 'Respuesta inválida del servidor';
      setError(message);
      return { success: false, message };
    } catch (loginError) {
      console.error('Login error:', loginError);
      const errorMsg = loginError.response?.data?.message ||
        loginError.message ||
        'Error de conexión';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const quickAccess = async (role) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiQuickAccess(role);

      if (data?.data?.user && data.data.token) {
        const token = data.data.token;
        applyAuthenticatedUser(data.data.user, token);
        const hydratedUser = await fetchCurrentUser(token);
        return { success: true, user: hydratedUser };
      }

      const message = data?.message || 'No se pudo completar el acceso rapido';
      setError(message);
      return { success: false, message };
    } catch (quickAccessError) {
      console.error('Quick access error:', quickAccessError);
      const errorMsg = quickAccessError.response?.data?.message ||
        quickAccessError.message ||
        'Error de conexion';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiRegister({
        name: `${userData.firstName} ${userData.lastName}`.trim(),
        email: userData.email,
        password: userData.password,
        role: userData.role || 'student'
      });

      if (response?.success && response?.data?.token) {
        const token = response.data.token;
        applyAuthenticatedUser(response.data.user, token);
        const hydratedUser = await fetchCurrentUser(token);
        return { success: true, user: hydratedUser };
      }

      return { success: false, message: response?.message || 'No se pudo registrar el usuario' };
    } catch (registerError) {
      console.error('Register error:', registerError);
      const message = registerError.response?.data?.message || registerError.message || 'Error al registrar';
      setError(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (logoutError) {
      console.error('Logout error:', logoutError);
    } finally {
      clearAuth();
    }
  };

  const updateCurrentUser = async (updates = null) => {
    try {
      const token = getStorage('authToken');

      if (!token) {
        throw new Error('No hay token disponible');
      }

      if (updates) {
        const response = await api.put('/users/me', updates);
        const updatedPayload = response.data?.success ? response.data.data : response.data;
        return applyAuthenticatedUser({
          ...currentUser,
          ...updatedPayload,
          studentCourses: currentUser?.studentCourses || [],
          instructorCourses: currentUser?.instructorCourses || []
        }, token);
      }

      return await fetchCurrentUser(token);
    } catch (updateError) {
      console.error('Error en updateCurrentUser:', updateError);
      if (updateError.response?.status === 401) {
        clearAuth();
      }
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        isLoading,
        error,
        login,
        quickAccess,
        register,
        logout,
        updateCurrentUser,
        clearError: () => setError(null)
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return context;
};
